import { Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/auth/authStore'
import { useDashboard, useMyMetrics } from '@/features/reports/api/useReports'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  FolderKanban,
  PlayCircle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Activity,
  AlertTriangle,
  BarChart3,
  Gauge,
  Zap,
} from 'lucide-react'

// =============================================================================
// StatCard
// =============================================================================

function StatCard({
  title,
  value,
  icon: Icon,
  description,
  href,
  colorClass = 'text-[#1976D2]',
}: {
  title: string
  value: string | number
  icon: React.ElementType
  description?: string
  href?: string
  colorClass?: string
}) {
  const content = (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`h-5 w-5 ${colorClass}`} />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  )

  if (href) {
    return <Link to={href}>{content}</Link>
  }
  return content
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-5 rounded" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-8 w-16 mb-1" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  )
}

// =============================================================================
// DashboardPage
//
// Data sources (reporting-service only):
//   GET /api/reports/dashboard  → DashboardStats
//   GET /api/reports/my-metrics → MyMetrics
//
// DashboardStats fields: total_projects, total_executions, passed, failed,
//   blocked, not_run, pass_rate, avg_pass_rate_7_days, avg_pass_rate_30_days,
//   test_coverage, tests_executed_today, pending_tests, test_velocity
//
// MyMetrics fields: tests_executed, pass_rate, avg_execution_time, defects_created
// =============================================================================

export default function DashboardPage() {
  const { user } = useAuthStore()

  const { data: dashboard, isLoading: dashboardLoading } = useDashboard()
  const { data: myMetrics, isLoading: myMetricsLoading } = useMyMetrics()

  const displayName = user?.first_name || user?.login?.split('@')[0] || 'User'
  const isLoading = dashboardLoading || myMetricsLoading

  const passRate = dashboard?.pass_rate ?? 0
  const passRateColor =
    passRate >= 80 ? 'text-green-600' : passRate >= 60 ? 'text-yellow-600' : 'text-red-600'
  const passRateProgressColor =
    passRate >= 80 ? 'bg-green-500' : passRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Welcome to QXProveIt</h1>
        <p className="mt-2 text-muted-foreground">
          Hello {displayName}! Here's your testing overview.
        </p>
      </div>

      {/* Overview Stats — DashboardStats fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title="Total Projects"
              value={dashboard?.total_projects ?? 0}
              icon={FolderKanban}
              href="/tests/projects"
              description="Active projects"
            />
            <StatCard
              title="Total Executions"
              value={dashboard?.total_executions ?? 0}
              icon={Activity}
              href="/executions/test-executions"
              description="All time"
            />
            <StatCard
              title="Executed Today"
              value={dashboard?.tests_executed_today ?? 0}
              icon={PlayCircle}
              description="Tests run today"
            />
            <StatCard
              title="Pending Tests"
              value={dashboard?.pending_tests ?? 0}
              icon={Clock}
              description="Awaiting execution"
            />
          </>
        )}
      </div>

      {/* Pass Rate & Execution Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pass Rate — DashboardStats: pass_rate, avg_pass_rate_7_days, avg_pass_rate_30_days */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#1976D2]" />
              Overall Pass Rate
            </CardTitle>
            <CardDescription>Based on all executed tests</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {dashboardLoading ? (
              <>
                <Skeleton className="h-10 w-20" />
                <Skeleton className="h-3 w-full" />
              </>
            ) : (
              <>
                <div className={`text-4xl font-bold ${passRateColor}`}>
                  {passRate.toFixed(1)}%
                </div>
                <div className="space-y-2">
                  <Progress
                    value={passRate}
                    className="h-3"
                    style={
                      { '--progress-color': passRateProgressColor } as React.CSSProperties
                    }
                  />
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>7-day avg: {(dashboard?.avg_pass_rate_7_days ?? 0).toFixed(1)}%</span>
                    <span>30-day avg: {(dashboard?.avg_pass_rate_30_days ?? 0).toFixed(1)}%</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Execution Breakdown — DashboardStats: passed, failed, blocked, not_run */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#1976D2]" />
              Execution Breakdown
            </CardTitle>
            <CardDescription>Status distribution across all executions</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Passed</span>
                  </div>
                  <span className="text-sm font-medium">{dashboard?.passed ?? 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm">Failed</span>
                  </div>
                  <span className="text-sm font-medium">{dashboard?.failed ?? 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm">Blocked</span>
                  </div>
                  <span className="text-sm font-medium">{dashboard?.blocked ?? 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">Not Run</span>
                  </div>
                  <span className="text-sm font-medium">{dashboard?.not_run ?? 0}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Coverage/Velocity & My Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coverage & Velocity — DashboardStats: test_coverage, test_velocity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-[#1976D2]" />
              Coverage &amp; Velocity
            </CardTitle>
            <CardDescription>Test coverage and execution speed</CardDescription>
          </CardHeader>
          <CardContent>
            {dashboardLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Test Coverage</p>
                    <p className="text-2xl font-bold">
                      {(dashboard?.test_coverage ?? 0).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Test Velocity</p>
                    <div className="flex items-center gap-1">
                      <p className="text-2xl font-bold">{dashboard?.test_velocity ?? 0}</p>
                      <Zap className="h-4 w-4 text-yellow-500" />
                    </div>
                    <p className="text-xs text-muted-foreground">tests/day</p>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Coverage</span>
                    <span className="font-medium">
                      {(dashboard?.test_coverage ?? 0).toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={dashboard?.test_coverage ?? 0} className="h-2" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Activity — MyMetrics: tests_executed, pass_rate, avg_execution_time, defects_created */}
        <Card>
          <CardHeader>
            <CardTitle>My Activity</CardTitle>
            <CardDescription>Your personal testing metrics</CardDescription>
          </CardHeader>
          <CardContent>
            {myMetricsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
              </div>
            ) : myMetrics ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Tests Executed</p>
                    <p className="text-2xl font-bold">{myMetrics.my_total_executions ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pass Rate</p>
                    <p className="text-2xl font-bold">
                      {(myMetrics.my_pass_rate ?? 0).toFixed(1)}%
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Execution Time</p>
                    <p className="text-lg font-semibold">{(myMetrics.my_executions_last_7_days ?? 0)}s</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Defects Created</p>
                    <p className="text-lg font-semibold">{myMetrics.my_assigned_testcases ?? 0}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No activity data available.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to="/tests/projects">Manage Projects</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/tests/test-cases">View Test Cases</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/executions/test-executions">View Executions</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/results/reports">View Reports</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/requirements-ops/req-specs">Requirements</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/compliance/configure-audit">Compliance</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
